import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react"
import { createPortal } from "react-dom"

interface PageHeaderSlotContextValue {
  slotElement: HTMLDivElement | null
  setSlotElement: (el: HTMLDivElement | null) => void
  active: boolean
  setActive: (v: boolean) => void
}

const PageHeaderSlotContext = createContext<PageHeaderSlotContextValue | null>(null)

export function PageHeaderSlotProvider({ children }: { children: ReactNode }) {
  const [slotElement, setSlotElement] = useState<HTMLDivElement | null>(null)
  const [active, setActive] = useState(false)
  return (
    <PageHeaderSlotContext.Provider
      value={{ slotElement, setSlotElement, active, setActive }}
    >
      {children}
    </PageHeaderSlotContext.Provider>
  )
}

export function usePageHeaderSlot() {
  const ctx = useContext(PageHeaderSlotContext)
  if (!ctx) {
    throw new Error("usePageHeaderSlot must be used within PageHeaderSlotProvider")
  }
  return ctx
}

/**
 * Pages render <PageHeaderSlot>...</PageHeaderSlot> to inject content into
 * the global header in place of the page title. Title is hidden while the
 * slot is mounted; restored on unmount.
 */
export function PageHeaderSlot({ children }: { children: ReactNode }) {
  const { slotElement, setActive } = usePageHeaderSlot()
  useEffect(() => {
    setActive(true)
    return () => setActive(false)
  }, [setActive])
  if (!slotElement) return null
  return createPortal(children, slotElement)
}
